import { allOutlineSegmentOptions } from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import { useEffect, useState } from "react";
import ClassifierService from "services/ClassifierService";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { Language } from "stores/ApplicationFilters/Filters/Language";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import { useStore } from "utils/useStore";
import type { CategoricalGroupByConfiguration } from "../../../SignalsModels";
import type { CategoricalGroupByOption } from "../../../Grouping/GroupByTypes";
import ChartCardStore from "../../ChartCard/ChartCardStore";
import { useGroupableExtendedMetadataFields } from "./useGroupableExtendedMetadataFields";
import {
    useQuestionResultGroupingOptions,
    type QuestionResultGroupingOptions,
} from "./useQuestionResultGroupingOptions";

export type GroupValueOption = {
    id: string;
    label: string;
};

export type GroupedAgentValueOption = {
    id: string;
    fullName: string;
    managerId?: string;
    managerFullName?: string;
};

const classifierService = new ClassifierService();

/** Everything a value source can read. */
type ValueSourceContext = {
    chartStore: ChartCardStore;
    filterValuesStore: ApplicationFilterValuesStore;
    applicationFiltersStore: ApplicationFiltersStore;
    /** Model Results outputs, fetched per selected classifier by this hook. */
    modelResults: { options: GroupValueOption[]; loading: boolean };
    /** Extended Metadata values for the selected field, loaded by this hook. */
    extendedMetadata: { options: GroupValueOption[]; loading: boolean };
    /** Question Result modules, questions, and answer options, sourced by their own hook. */
    questionResult: QuestionResultGroupingOptions;
};

/**
 * Where one dimension's selectable values come from.
 *
 * The `source` discriminant is what keeps a dimension's values and its loading state declared
 * together: `async` *requires* a `loading` predicate, so adding a dimension backed by a fetch
 * without saying how to tell it is still in flight does not compile. Getting that wrong used to be
 * invisible -- an unloaded list looks empty, and an empty list of valid values silently discards
 * every saved selection.
 */
type ValueSource =
    /** Values are always available; there is nothing to wait for. */
    | {
          source: "static";
          options: (context: ValueSourceContext) => GroupValueOption[];
      }
    /** Values arrive from a store or service, so "empty" and "not yet loaded" differ. */
    | {
          source: "async";
          options: (context: ValueSourceContext) => GroupValueOption[];
          loading: (context: ValueSourceContext) => boolean;
      }
    /**
     * Values are chosen through a dedicated selector rather than the shared picker, so this option
     * has no generic value list and saved ids must not be validated against one.
     */
    | { source: "custom" };

/**
 * Additional context a dimension needs before its values mean anything -- a model for Model
 * Results, a field for Extended Metadata. Until it is chosen, saved ids are kept rather than
 * filtered, because there is nothing meaningful to filter against.
 */
const hasPrerequisite = (draft: CategoricalGroupByConfiguration): boolean => {
    switch (draft.option) {
        case "ModelResults":
            return !!draft.llmClassifierId;
        case "ExtendedMetadata":
            return !!draft.extendedMetadataKey;
        // Two prerequisites, and the answer options only mean anything once both are chosen: a
        // module narrows the question list, and the question owns the options themselves.
        case "QuestionResult":
            return !!draft.licensedModuleId && !!draft.questionId;
        default:
            return true;
    }
};

const CATEGORICAL_VALUE_SOURCES: Record<CategoricalGroupByOption, ValueSource> =
    {
        // Hierarchy uses AcxHierarchySelector and Agent a grouped agent picker, both of which
        // supply their own ids.
        Hierarchy: { source: "custom" },
        Agent: { source: "custom" },

        // Managers are a flat list, unlike agents, so this is an ordinary async source and the
        // shared picker handles it -- an agent nests under the manager it has, and a manager has
        // no parent to nest under. Same reasoning AgentManagersFilter records for the filter.
        // The list is loaded eagerly by ApplicationFilterValuesStore's constructor.
        AgentManager: {
            source: "async",
            options: ({ filterValuesStore }) =>
                filterValuesStore.agentManagers.map((manager) => ({
                    id: manager.id,
                    label: manager.fullName?.trim() || "Unknown Agent Manager",
                })),
            loading: ({ filterValuesStore }) =>
                filterValuesStore.getTaskLoading(
                    ApplicationFilterValuesStore.Tasks.LOAD_AGENT_MANAGERS,
                ),
        },

        ConversationLabeling: {
            source: "async",
            options: ({ filterValuesStore }) =>
                filterValuesStore.dataLabelingModules.map((module) => ({
                    id: module.id,
                    label: module.name,
                })),
            loading: ({ filterValuesStore }) =>
                filterValuesStore.getTaskLoading(
                    ApplicationFilterValuesStore.Tasks.LOAD_DATA_LABELING_MODULES,
                ),
        },
        EvaluationType: {
            source: "static",
            options: () => [
                {
                    id: "MiddleOfTheFunnel",
                    label: EvaluationType.toString(EvaluationType.AIEvaluated),
                },
                {
                    id: "BottomOfTheFunnel",
                    label: EvaluationType.toString(
                        EvaluationType.HumanEvaluated,
                    ),
                },
                { id: "NotEvaluated", label: "Not Evaluated" },
            ],
        },
        Language: {
            source: "static",
            options: ({ applicationFiltersStore }) =>
                applicationFiltersStore.languageOptions.map((option) => ({
                    id:
                        option.value === Language.NonEnglish
                            ? "Non-English"
                            : "English",
                    label: option.label,
                })),
        },
        MediaType: {
            source: "static",
            options: () =>
                [MediaType.Audio, MediaType.ProcessedChat].map((mediaType) => ({
                    id: MediaType[mediaType],
                    label: MediaType.toString(mediaType),
                })),
        },
        PrimaryThemes: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.topics.map((topic) => ({
                    id: topic.id,
                    label: topic.topicLabel,
                })),
            loading: ({ chartStore }) => chartStore.optionsStore.topicsLoading,
        },
        Themes: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.themes.map((theme) => ({
                    id: theme.id,
                    label: theme.themeLabel,
                })),
            loading: ({ chartStore }) => chartStore.optionsStore.themesLoading,
        },
        Outline: {
            source: "static",
            options: () =>
                allOutlineSegmentOptions.map((segment) => ({
                    id: segment.id,
                    label: segment.name,
                })),
        },
        PatientJourney: {
            source: "static",
            options: ({ applicationFiltersStore }) =>
                applicationFiltersStore.journeyStageOptions.map((option) => ({
                    id: option.label,
                    label: option.label,
                })),
        },
        EddyEffect: {
            source: "static",
            options: () => [
                { id: "identified", label: "Eddy Identified" },
                { id: "not_identified", label: "Eddy Not Identified" },
            ],
        },
        CallDirection: {
            source: "static",
            options: () => [
                { id: "Inbound", label: "Inbound" },
                { id: "Outbound", label: "Outbound" },
                { id: "Transfer", label: "Transfer" },
                { id: "Other", label: "Other" },
            ],
        },
        ContactType: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.customerTypeOptions.map((option) => ({
                    id: option.value,
                    label: option.label,
                })),
            loading: ({ chartStore }) =>
                chartStore.optionsStore.customerTypesLoading,
        },
        Classifiers: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.userClassifiers.map((classifier) => ({
                    id: classifier.id,
                    label: classifier.name,
                })),
            loading: ({ chartStore }) =>
                chartStore.optionsStore.userClassifiersLoading,
        },
        ModelResults: {
            source: "async",
            options: ({ modelResults }) => modelResults.options,
            loading: ({ modelResults }) => modelResults.loading,
        },
        EndingSentiment: {
            source: "static",
            options: () => [
                { id: "Positive", label: "Positive" },
                { id: "Neutral", label: "Neutral" },
                { id: "Negative", label: "Negative" },
            ],
        },
        StartingSentiment: {
            source: "static",
            options: () => [
                { id: "Positive", label: "Positive" },
                { id: "Neutral", label: "Neutral" },
                { id: "Negative", label: "Negative" },
            ],
        },
        HipaaCompliance: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.hipaaOptions.map((option) => ({
                    id: option.id,
                    label: option.label,
                })),
            loading: ({ chartStore }) =>
                chartStore.optionsStore.hipaaOptionsLoading,
        },
        SafetyEvent: {
            source: "static",
            options: () => [
                { id: "1", label: "Identified" },
                { id: "0", label: "Not Identified" },
            ],
        },
        PrimaryTopic: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.primaryTopics.map((topic) => ({
                    id: String(topic.topicNumber),
                    label: topic.topicLabel,
                })),
            loading: ({ chartStore }) =>
                chartStore.optionsStore.getTaskLoading(
                    "options:load primary topics",
                ),
        },
        PrimaryDriver: {
            source: "async",
            options: ({ chartStore }) =>
                chartStore.optionsStore.primaryDrivers.map((driver) => ({
                    id: String(driver.driverIndex),
                    label: driver.driverName,
                })),
            loading: ({ chartStore }) =>
                chartStore.optionsStore.getTaskLoading(
                    "options:load primary drivers",
                ),
        },
        ExtendedMetadata: {
            source: "async",
            options: ({ extendedMetadata }) => extendedMetadata.options,
            loading: ({ extendedMetadata }) => extendedMetadata.loading,
        },
        QuestionResult: {
            source: "async",
            options: ({ questionResult }) => questionResult.tagOptions,
            // The answer options live on the module details, so they are still arriving for as long
            // as those are.
            loading: ({ questionResult }) => questionResult.questionsLoading,
        },
    };

/**
 * Sources the selectable values for the dimension being configured, and says whether they are still
 * arriving.
 *
 * Each dimension reads its values from somewhere different -- an org options store, a filter values
 * store, a service call, or a fixed list -- so the per-option table above is the single place that
 * mapping lives. Values are read during render rather than memoized so MobX tracks them directly;
 * a hand-maintained dependency list is the other way this logic used to go stale.
 */
export const useCategoricalValueOptions = (
    chartStore: ChartCardStore,
    draft: CategoricalGroupByConfiguration,
) => {
    const filterValuesStore = useStore(ApplicationFilterValuesStore);
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const groupableExtendedMetadataFields =
        useGroupableExtendedMetadataFields();
    const questionResult = useQuestionResultGroupingOptions(draft);

    const option = draft.option;
    const extendedMetadataKey = draft.extendedMetadataKey;
    const llmClassifierId = draft.llmClassifierId;

    useEffect(() => {
        if (option === "ConversationLabeling") {
            void filterValuesStore.loadDataLabelingModules();
        }
    }, [filterValuesStore, option]);

    const extendedMetadataClassifierId = groupableExtendedMetadataFields.find(
        (field) => field.name === extendedMetadataKey,
    )?.id;

    useEffect(() => {
        if (
            option !== "ExtendedMetadata" ||
            !extendedMetadataKey ||
            !extendedMetadataClassifierId
        ) {
            return;
        }
        void filterValuesStore.loadExtendedMetadataGroupedList(
            `extendedMetadata.${extendedMetadataKey}`,
            extendedMetadataClassifierId,
        );
    }, [
        extendedMetadataClassifierId,
        extendedMetadataKey,
        filterValuesStore,
        option,
    ]);

    // Read the entry during render so MobX tracks the property-level access and re-renders when a
    // new key is set on the record; reading it inside a memo would not.
    const extendedMetadataList = extendedMetadataKey
        ? filterValuesStore.extendedMetadataGroupedLists[
              `extendedMetadata.${extendedMetadataKey}`
          ]
        : undefined;

    const [modelResultOptions, setModelResultOptions] = useState<
        GroupValueOption[]
    >([]);
    const [modelResultOptionsLoading, setModelResultOptionsLoading] =
        useState(false);
    const [modelResultsLoadedForClassifier, setModelResultsLoadedForClassifier] =
        useState<string | undefined>();

    useEffect(() => {
        if (option !== "ModelResults") {
            setModelResultOptions([]);
            setModelResultOptionsLoading(false);
            setModelResultsLoadedForClassifier(undefined);
            return;
        }

        if (!llmClassifierId) {
            setModelResultOptions([]);
            setModelResultsLoadedForClassifier(undefined);
            return;
        }

        let disposed = false;
        setModelResultOptions([]);
        setModelResultsLoadedForClassifier(undefined);
        setModelResultOptionsLoading(true);

        void classifierService
            .getClassifierOutputs(llmClassifierId)
            .then((results) => {
                if (disposed) return;
                setModelResultOptions(
                    (results ?? []).map((result) => ({
                        id: result.result,
                        label: result.result,
                    })),
                );
                setModelResultsLoadedForClassifier(llmClassifierId);
            })
            .finally(() => {
                if (!disposed) {
                    setModelResultOptionsLoading(false);
                }
            });

        return () => {
            disposed = true;
        };
    }, [llmClassifierId, option]);

    const context: ValueSourceContext = {
        chartStore,
        filterValuesStore,
        applicationFiltersStore,
        modelResults: {
            options: modelResultOptions,
            // Also loading between picking a model and its outputs arriving, which is not yet
            // reflected by the request flag.
            loading:
                !!llmClassifierId &&
                (modelResultOptionsLoading ||
                    modelResultsLoadedForClassifier !== llmClassifierId),
        },
        extendedMetadata: {
            options:
                option === "ExtendedMetadata"
                    ? (extendedMetadataList ?? []).map((item) => ({
                          id: item.result,
                          label: item.result,
                      }))
                    : [],
            loading:
                !!extendedMetadataKey &&
                !extendedMetadataList &&
                filterValuesStore.getTaskLoading(
                    ApplicationFilterValuesStore.Tasks
                        .LOAD_EXTENDED_METADATA_GROUPED_LIST,
                ),
        },
        questionResult,
    };

    const valueSource = CATEGORICAL_VALUE_SOURCES[option];

    // Only built for Agent grouping, which is the one option that uses it -- an org's agent list
    // is long enough that reshaping it for every other dimension is wasted work.
    const groupedAgentValueOptions: GroupedAgentValueOption[] =
        option === "Agent"
            ? (filterValuesStore.agents ?? []).map((agent) => ({
                  id: agent.id,
                  fullName: agent.fullName,
                  managerId: agent.managerId,
                  // Endpoints that project the manager's name send it flat; the rest expand
                  // `manager`, so fall back to assembling it from there.
                  managerFullName:
                      agent.managerFullName ||
                      [agent.manager?.firstName, agent.manager?.lastName]
                          .filter(Boolean)
                          .join(" ")
                          .trim() ||
                      agent.manager?.userName,
              }))
            : [];

    return {
        /** Selectable values for the shared picker; empty for `custom` sources. */
        valueOptions:
            valueSource.source === "custom" ? [] : valueSource.options(context),
        valuesLoading:
            valueSource.source === "async" ? valueSource.loading(context) : false,
        /**
         * Whether saved ids may be validated against `valueOptions`. False while a `custom` source
         * owns them, or while a prerequisite is unchosen and the list is meaningless.
         */
        canValidateSelectedIds:
            valueSource.source !== "custom" && hasPrerequisite(draft),
        groupedAgentValueOptions,
        agentsLoading: filterValuesStore.getTaskLoading(
            ApplicationFilterValuesStore.Tasks.LOAD_AGENTS,
        ),
        /**
         * The module and question lists behind Question Result's own selectors. Returned here, like
         * the grouped agent options above, so the editor sources everything from one hook call
         * rather than loading the same module details twice.
         */
        questionResult,
    };
};
